import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PlayCircle, ExternalLink, Youtube, CalendarDays } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { api, fileUrl } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Shared';

// One embedded YouTube video with its title. When the embed is blocked or
// the slot has only a thumbnail, we show the thumbnail + a Watch link.
const VideoTile = ({ v, large = false }) => (
  <Card className={`overflow-hidden ${large ? '' : 'card-lift'}`} data-testid="video-card" data-video-id={v.id}>
    {v.embed_url && !v.embed_blocked ? (
      <div className="aspect-video bg-black">
        <iframe src={fileUrl(v.embed_url)} title={v.title} className="w-full h-full" loading="lazy" allowFullScreen allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" data-testid="video-iframe" />
      </div>
    ) : (
      <a href={v.watch_url || '#'} target="_blank" rel="noreferrer" className="block aspect-video bg-[#1a2744] relative" data-testid="video-thumb-link">
        {v.thumbnail_url && <img src={fileUrl(v.thumbnail_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" loading="lazy" />}
        <span className="absolute inset-0 flex flex-col items-center justify-center text-white">
          <PlayCircle className="h-14 w-14 text-[#C9A84C]" />
          <span className="mt-2 text-sm font-bold">Watch on YouTube</span>
        </span>
      </a>
    )}
    <CardContent className={large ? 'p-5' : 'p-4'}>
      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
        <Badge variant="outline" className="border-slate-300 text-slate-600">{v.category_label}</Badge>
        {v.published_on && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {v.published_on}</span>}
      </div>
      <p className={`font-serif font-bold text-[#1a2744] leading-snug ${large ? 'text-lg' : ''}`} data-testid="video-title">{v.title}</p>
      {v.description && <p className={`text-sm text-slate-600 mt-1 leading-relaxed ${large ? '' : 'line-clamp-3'}`}>{v.description}</p>}
      {v.watch_url && <a href={v.watch_url} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs text-slate-500 underline underline-offset-2 mt-2" data-testid="video-watch-link">Open on YouTube <ExternalLink className="h-3 w-3 ml-1" /></a>}
    </CardContent>
  </Card>
);

const CollectionView = ({ col, category, setCategory }) => {
  const cats = col.groups.map((g) => ({ key: g.key, label: g.label, count: g.videos.length }));
  const groups = category === 'all' ? col.groups : col.groups.filter((g) => g.key === category);
  const total = col.count;
  if (total === 0) {
    return <EmptyState title={`No ${col.label.toLowerCase()} published yet`} subtitle="New videos appear here as soon as they are published from Admin." />;
  }
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Video categories" data-testid="video-category-chips">
        <button type="button" onClick={() => setCategory('all')} className={`h-10 px-4 rounded-full border text-sm font-bold btn-press ${category === 'all' ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:border-[#1a2744]'}`} data-testid="video-category-all" aria-pressed={category === 'all'}>All ({total})</button>
        {cats.map((c) => (
          <button key={c.key} type="button" onClick={() => setCategory(c.key)} className={`h-10 px-4 rounded-full border text-sm font-bold btn-press ${category === c.key ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:border-[#1a2744]'}`} data-testid={`video-category-${c.key}`} aria-pressed={category === c.key}>{c.label} ({c.count})</button>
        ))}
      </div>
      {category === 'all' && col.featured && (
        <div className="mb-8" data-testid="featured-video-card"><VideoTile v={col.featured} large /></div>
      )}
      {groups.length === 0 ? (
        <EmptyState title="No videos in this category yet" subtitle="Pick another category above." />
      ) : groups.map((g) => (
        <section key={g.key} className="mb-10" data-testid={`video-group-${g.key}`}>
          <h2 className="text-xl font-bold text-[#1a2744] mb-3">{g.label} <span className="text-slate-400 font-semibold">({g.videos.length})</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {g.videos.filter((v) => !(category === 'all' && col.featured && v.id === col.featured.id)).map((v) => <VideoTile key={v.id} v={v} />)}
          </div>
        </section>
      ))}
    </div>
  );
};

// Video Library: Steve Lesperance Videos (his channel) clearly separated from
// Other CKL Election Videos / Candidate Events. Admin adds videos by pasting a
// YouTube link; no code changes needed.
export default function Videos() {
  const [params, setParams] = useSearchParams();
  const collection = params.get('collection') === 'other' ? 'other' : 'steve';
  const category = params.get('category') || 'all';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true); setError(false);
    api.get('/videos/library').then((r) => setData(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setParam = (k, v, reset = false) => {
    const next = reset ? new URLSearchParams() : new URLSearchParams(params);
    if (!v || v === 'all' || (k === 'collection' && v === 'steve')) next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };
  const col = useMemo(() => (data?.collections || []).find((c) => c.key === collection), [data, collection]);
  const other = useMemo(() => (data?.collections || []).find((c) => c.key !== collection), [data, collection]);

  return (
    <div>
      <PageHeader pageKey={collection === 'steve' ? 'videos' : ''} kicker="VIDEO LIBRARY" title={collection === 'steve' ? 'Steve Lesperance Videos' : 'Other CKL Election Videos / Candidate Events'} subtitle={collection === 'steve' ? "Steve's messages, accountability, taxes and spending, Paper Ballots, PITA, Ward 8 issues and election events, all in one place." : 'Forums, debates and candidate events from across Kawartha Lakes, plus City Council streams. Not endorsements.'} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Collection switch */}
        <div className="flex flex-wrap items-center gap-2 mb-6" role="tablist" aria-label="Video collections" data-testid="video-collection-tabs">
          {(data?.collections || [{ key: 'steve', label: 'Steve Lesperance Videos', count: 0 }, { key: 'other', label: 'Other CKL Election Videos / Candidate Events', count: 0 }]).map((c) => (
            <button key={c.key} type="button" role="tab" aria-selected={collection === c.key} onClick={() => { setParam('collection', c.key, true); }} className={`h-11 px-5 rounded-xl border-2 text-sm font-bold btn-press ${collection === c.key ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:border-[#1a2744]'}`} data-testid={`video-collection-${c.key}`}>
              {c.label} <span className={collection === c.key ? 'text-[#C9A84C]' : 'text-slate-400'}>({c.count})</span>
            </button>
          ))}
          {data?.channel_url && collection === 'steve' && (
            <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold ml-auto" data-testid="video-channel-link">
              <a href={data.channel_url} target="_blank" rel="noreferrer"><Youtube className="h-4 w-4 mr-1.5" /> Steve's YouTube channel</a>
            </Button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="videos-loading">{[0, 1, 2].map((i) => <Skeleton key={i} className="aspect-video rounded-2xl" />)}</div>
        ) : error || !data ? (
          <div className="text-center py-10" data-testid="videos-error">
            <p className="font-bold text-[#1a2744]">Videos could not be loaded.</p>
            <Button onClick={load} variant="outline" className="mt-3 h-11 border-[#1a2744] text-[#1a2744] font-bold" data-testid="videos-retry">Try again</Button>
          </div>
        ) : col ? (
          <CollectionView col={col} category={category} setCategory={(c) => setParam('category', c)} />
        ) : null}

        {other && other.count > 0 && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-[#f3f7ff] p-5 flex flex-wrap items-center justify-between gap-3" data-testid="video-other-collection-teaser">
            <p className="text-sm text-slate-700"><strong className="text-[#1a2744]">{other.label}</strong>: {other.count} video{other.count === 1 ? '' : 's'}.</p>
            <Button variant="outline" onClick={() => setParam('collection', other.key, true)} className="h-10 border-[#1a2744] text-[#1a2744] font-bold" data-testid="video-switch-collection">View</Button>
          </div>
        )}
        <p className="mt-8 text-xs text-slate-500 leading-relaxed max-w-3xl">
          Looking for a debate or meet and greet near you? See the <Link to="/elections/events" className="underline font-semibold text-[#1a2744]" data-testid="videos-events-link">Election Event List</Link>. Recordings shared after an event are linked from the event itself.
        </p>
      </div>
    </div>
  );
}
